import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { IDiagnosticData, IInternalData, IOption, IPreRequisiteQuestion, IProcessedData, IQuestion } from '../interfaces/IQuestion';
import authContext from '../shared/src/contexts/authContext';
import QuestionTypes from '../utils/questionTypeContants';

type TUseForm = {
	getQuestions: () => Promise<any>
	sendAnswers: (answers: IProcessedData[]) => Promise<any>
	onSaveAnswers?: any;
	onFinishRedirect: string
}

const useForm = ({getQuestions, sendAnswers, onFinishRedirect, onSaveAnswers = null}: TUseForm) => {
	const [loading, setLoading] = useState(true);
	const [formQuestions, setFormQuestions] = useState<any>(null);
	const [formData, setFormData] = useState<IDiagnosticData>({} as IDiagnosticData);
	const [headers, setHeaders] = useState<any>([]);
	const appContext = useContext(authContext);
	const [step, setStep] = useState(0);
	const parentRef = useRef(null);
	const navigate = useNavigate();
	const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);

	const setData = (value: any, field: string, type: any = null) => {
		let parsedValue: IInternalData;

		try {
			parsedValue = JSON.parse(value);
		} catch (error) {
			parsedValue = value;
		}

		const newFormData = JSON.parse(JSON.stringify(formData));
		if (!type) {
			newFormData[field] = parsedValue;
		} else if (type === "checkbox") {

			if (newFormData[field] === undefined) {
				newFormData[field] = [parsedValue];
			} else {
				const index = newFormData[field].findIndex((el) => el.id === parsedValue.id)
				if (index >= 0) {
					newFormData[field].splice(index, 1);
				} else newFormData[field] = [...newFormData[field], parsedValue];
			}
		}
		setFormData(newFormData);
	}

	const processFormData = (formData: IDiagnosticData) => {
		const processedData: Array<IProcessedData> = [];

		// Convert to required format to be able to send it to the server
		for (const key in formData) {
			const value = formData[key];
			// Check if value is an array and add all options
			if (Array.isArray(value)) {
				(value as Array<IInternalData>).forEach(element => {
					processedData.push({
						idQuestion: key,
						idOption: element.id,
						openResponse: element.name
					});
				});
				// Check if its an object and add its value
			} else if (value === Object(value)) {
				processedData.push({
					idQuestion: key,
					idOption: (value as IInternalData).id,
					openResponse: (value as IInternalData).name
				});
				// Its a primitive
			} else {
				processedData.push({
					idQuestion: key,
					idOption: null,
					openResponse: value + ""
				});
			}
		}
		return processedData;
	}

	const handleContinueResults = async () => {
		setLoading(true);

		if (!appContext.gigyaAccount) {
			return;
		}

		try {
			const processedData: Array<IProcessedData> = processFormData(formData);

			const response = await sendAnswers( processedData);

			if (response.status === 200) {
				navigate(onFinishRedirect)
			}

		} catch (error) {
			console.error(error);
			alert('Error al guardar los resultados, vuelve a intentarlo nuevamente');
		}
		finally {
			setLoading(false);
		}
	}

	const preloadFormData = (questions: Array<IQuestion>) => {
		let formDataPreloaded = {}
		// Preload form data
		questions.forEach((question) => {
			// Preload single answer open questions
			if (question?.openResponse) {
				formDataPreloaded[question.id] = question.openResponse;
				return;
			}
			// Preload multiple answer closed questions
			if (question?.options && question.type.id === 4) {
				let selectedOptions = []
				question.options.forEach((option) => {
					if (option?.selected) {
						selectedOptions.push({ id: option.id, name: option.name })
					}
				})
				if (selectedOptions.length > 0) {
					formDataPreloaded[question.id] = selectedOptions;
				}
			}

			// Preload single answer closed questions
			if (question?.options && question.type.id !== 4) {
				let selectedOption = null;
				question.options.forEach((option) => {
					if (option?.selected) {
						selectedOption = { id: option.id, name: option.name }
					}
				})
				if (selectedOption) {
					formDataPreloaded[question.id] = selectedOption;
				}
			}

			// Preload multiple child question with closed questions
			if (question?.childQuestions) {
				let selectedResponses = {};

				question.childQuestions.forEach((childQuestion) => {
					childQuestion.options.forEach((childQuestionOption) => {
						if (childQuestionOption?.selected) {
							selectedResponses[childQuestion.id] = { id: childQuestionOption.id, name: childQuestionOption.name }
						}
					})
				});
				if (Object.keys(selectedResponses).length > 0) {
					formDataPreloaded = { ...formDataPreloaded, ...selectedResponses };
				}
			}
		})
		return formDataPreloaded;
	}

	// Check if an item is selected in options array
	const checkIfOptionsSelected = (options: Array<IOption>) => {
		return options.some((option) => option.selected);
	}

	//Check if a question has prerequisites
	const checkPreRequisiteQuestions = (
		preRequisiteQuestions: IPreRequisiteQuestion, // Ahora es un array de preguntas
		formData: IDiagnosticData
	  ) => {
		return preRequisiteQuestions.every((preRequisiteQuestion) => {
		  
		  // Obtener los IDs de las opciones de la pregunta previa
		  const preRequisiteQuestionIds = preRequisiteQuestion.options.map((option) => option.id);
		  const formDataResponse: any = formData[preRequisiteQuestion.idQuestion];
	  
		  if (!formDataResponse) {
			// Si no hay respuesta en formData para la pregunta, no pasa la validación
			return false;
		  }
	  
		  // Verificar si formData contiene un array de respuestas
		  if (Array.isArray(formData[preRequisiteQuestion.idQuestion])) {
			// Comprobar si alguna respuesta coincide con los IDs de las opciones
			return formDataResponse.some((data: IInternalData) => preRequisiteQuestionIds.includes(data.id));
		  }
	  
		  // Si formData no es un array, comprobar si el ID de la respuesta coincide con algún ID de opción
		  return preRequisiteQuestionIds.includes(formDataResponse.id);
		});
	  };
	  

	// Calculate the index in which the user should start the form based on answered questions
	const calculateFormInitialIndex = (questions: Array<IQuestion>) => {
		let currentPurposeId = questions[0].purpose;
		let currentPurposeIdx = 0;

		for (let i = 0; i < questions.length; i++) {
			let question: IQuestion = questions[i];

			if (i === questions.length - 1) {
				currentPurposeId = question.purpose;
				currentPurposeIdx = i;
				break;
			}

			if (question.purpose !== currentPurposeId) {
				currentPurposeId = question.purpose;
				currentPurposeIdx = i;
			}

			if ([QuestionTypes['did you know'], QuestionTypes.title].includes(question.type.id) || !question.required) {
				continue;
			}

			if (question.hasOwnProperty('preRequisiteQuestions') && !checkPreRequisiteQuestions(question.preRequisiteQuestions, formData)) {
				continue;
			}

			if (!question.hasOwnProperty('openResponse') && !question.hasOwnProperty('options') && !question.hasOwnProperty('childQuestions')) {
				break;
			}

			if (question.hasOwnProperty('options') && !checkIfOptionsSelected(question.options)) {
				break;
			}

			if (question.hasOwnProperty('childQuestions')) {
				let answered = question.childQuestions.every((childQuestion) =>
					childQuestion.options.some((childQuestionOption) =>
						childQuestionOption?.selected
					)
				);
				if (!answered) {
					break;
				}
			}
		}

		return currentPurposeIdx;
	}

	const getData = async () => {
		if (!appContext.gigyaAccount) {
			return;
		}

		try {
			const response = await getQuestions();

			const questions: any = []
			const tempHeaders: any = [];

			response?.data?.data.forEach((purpose: any) => {
				questions.push(...purpose.questions);
				tempHeaders.push({ title: purpose.name, value: purpose.id });
			});

			let formDataPreloaded = preloadFormData(questions);
			let currentQuestionIdx = calculateFormInitialIndex(questions)
			setCurrentQuestionIdx(currentQuestionIdx);
			setFormData(formDataPreloaded);
			setHeaders(tempHeaders);
			setFormQuestions(questions);
			setLoading(false);

		} catch (error) {
			console.error(error);
			setLoading(false);
		}
	}

	useEffect(() => {
		getData();
	}, [appContext.gigyaAccount])

	const saveFormData = async () => {
		if (!appContext.gigyaAccount)
			return;

		if (step === 0 || step === headers[0].value || Object.keys(formData).length === 0)
			return;

		try {
			const processedData: Array<IProcessedData> = processFormData(formData);
			await sendAnswers(processedData);
			if(onSaveAnswers){
				onSaveAnswers(); // get and notify Points
			}
		} catch (error) {
			console.error(error);
		}
	}

	useEffect(() => {
		saveFormData();
	}, [step])

	return {
		formData,
		headers,
		step,
		setStep,
		loading,
		formQuestions,
		parentRef,
		currentQuestionIdx,
		handleContinueResults,
		appContext,
		setData
	}

}

export default useForm;